import React, { useCallback, useEffect, useState } from 'react';
import { Swords, Users, Star, Loader2, X, Crown } from 'lucide-react';
import { gbApi, gbFmt } from './gbApi';
import { GbCoin } from './GbCoin';
import { GbRankRules } from './GbRankRules';

const SUBS = [
  { id: 'level', label: 'NÍVEL', legenda: 'xp', Icon: Star },
  { id: 'pvp', label: 'PVP', legenda: 'vitórias', Icon: Swords },
  { id: 'clan', label: 'CLÃS', legenda: 'pontos', Icon: Users },
];

const TITULOS = {
  level: { nome: 'RANKING DE NÍVEL', sub: 'quem acumulou mais xp na temporada', Icon: Star },
  pvp: { nome: 'RANKING DO PVP', sub: 'quem venceu mais duelos ao vivo', Icon: Swords },
  clan: { nome: 'RANKING DOS CLÃS', sub: 'pontos somados dos membros', Icon: Users },
};

const Face = ({ row, cls }) => (
  row?.avatar
    ? <img src={row.avatar} alt="" className={`${cls} rounded-full object-cover`} />
    : <div className={`${cls} flex items-center justify-center rounded-full bg-gradient-to-b from-purple-900 to-black leading-none text-amber-400/90`}
      style={row?.hex ? { background: row.hex } : undefined}>
      <span className="font-black tracking-tight text-black/85 drop-shadow" style={{ fontSize: '0.8em' }}>🎃</span>
    </div>
);

/* posições medidas em pixels sobre a arte do pódio (1100x696) */
const PW = 1100, PH = 696;
const box = (x, y, w, h) => ({
  left: `${(x / PW) * 100}%`, top: `${(y / PH) * 100}%`,
  width: `${(w / PW) * 100}%`, ...(h ? { height: `${(h / PH) * 100}%` } : {}),
});
const POD = [
  { circle: box(451, 155, 198), lead: box(576, 258, 112), plate: box(365, 428, 370, 70), big: true },
  { circle: box(125, 251, 154), lead: box(214, 330, 86), plate: box(100, 458, 232, 45) },
  { circle: box(823, 251, 152), lead: box(911, 330, 86), plate: box(768, 458, 230, 45) },
];

const Podium = ({ rows, label, value, medal, onPick }) => (
  <div className="relative mx-auto w-full max-w-[430px]" style={{ aspectRatio: `${PW}/${PH}` }}
    data-testid="gb-podium">
    {POD.map((pos, i) => rows[i] && (
      <div key={`av-${i}`} className="absolute z-0 overflow-hidden rounded-full"
        style={{ ...pos.circle, aspectRatio: '1/1', fontSize: pos.big ? 'clamp(20px,7vw,54px)' : 'clamp(16px,5.5vw,42px)' }}
        data-testid={`gb-podium-avatar-${i + 1}`}>
        <Face row={rows[i]} cls="h-full w-full" />
      </div>
    ))}
    {POD.map((pos, i) => (rows[i] && medal?.(rows[i])) && (
      <div key={`medal-${i}`} data-testid={`gb-podium-medal-${i + 1}`}
        className="absolute z-[15]" style={{ ...pos.lead, aspectRatio: '1/1' }}>
        <img src={medal(rows[i])} alt="" title="medalha do clã"
          className="h-full w-full object-contain drop-shadow-[0_0_8px_rgba(255,190,60,.6)]" />
      </div>
    ))}
    <img src="/oil/podium_top3.webp" alt="" className="pointer-events-none absolute inset-0 z-10 h-full w-full object-contain drop-shadow-2xl" />
    {POD.map((pos, i) => rows[i] && (
      <div key={`plate-${i}`} onClick={() => onPick?.(rows[i])}
        className={`absolute z-20 flex flex-col items-center justify-center overflow-hidden px-[2%] leading-none ${onPick ? 'cursor-pointer' : ''}`}
        style={pos.plate} data-testid={`gb-podium-${i + 1}`}>
        <p className="w-full truncate text-center font-black text-amber-100"
          style={{ fontSize: (() => {
            const n = String(label(rows[i]) || '').length;
            const enc = n >= 26 ? 0.72 : n >= 18 ? 0.85 : 1;
            return pos.big ? `clamp(${8 * enc}px,${2.9 * enc}vw,${13 * enc}px)`
              : `clamp(${6.5 * enc}px,${2.1 * enc}vw,${10 * enc}px)`;
          })() }}>
          {label(rows[i])}
        </p>
        <p className="mt-[0.35em] w-full truncate text-center font-black text-amber-400"
          style={{ fontSize: pos.big ? 'clamp(8px,2.4vw,11px)' : 'clamp(6px,1.85vw,9px)' }}>
          {value(rows[i])}
        </p>
      </div>
    ))}
  </div>
);

const RankList = ({ rows, label, value, medal, startAt, meuId, idOf, onPick }) => (
  <div className="relative mx-auto -mt-1 w-full max-w-[430px] rounded-[26px] border border-purple-800/70 bg-gradient-to-b from-[#170d25] to-[#0a0611] p-2 shadow-[0_14px_34px_rgba(0,0,0,.6)]"
    data-testid="gb-rank-list-frame">
    <span className="pointer-events-none absolute inset-x-10 -top-px h-[2px] rounded-full bg-gradient-to-r from-transparent via-orange-500/80 to-transparent" />
    <span className="pointer-events-none absolute inset-x-16 -bottom-px h-[2px] rounded-full bg-gradient-to-r from-transparent via-purple-600/70 to-transparent" />
    <div className="max-h-[300px] overflow-y-auto rounded-[20px] px-1 py-1 [scrollbar-color:rgba(255,146,40,.5)_transparent] [scrollbar-width:thin]"
      data-testid="gb-rank-list">
      {rows.length === 0 ? (
        <p className="py-5 text-center text-[11px] font-bold text-purple-300/60" data-testid="gb-rank-list-empty">
          Ninguém mais no ranking ainda.
        </p>
      ) : rows.map((r, i) => {
        const rank = startAt + i;
        const eu = meuId && idOf(r) === meuId;
        return (
          <div key={`${idOf(r)}-${rank}`} data-testid={`gb-rank-row-${idOf(r)}`}
            onClick={() => onPick?.(r)}
            className={`mb-1 flex items-center gap-2 rounded-lg border px-2 py-1.5 ${onPick ? 'cursor-pointer' : ''} ${eu
              ? 'border-orange-500/80 bg-orange-950/40'
              : rank <= 10 ? 'border-amber-700/60 bg-gradient-to-r from-amber-950/60 to-black'
                : 'border-purple-900/70 bg-black/40'}`}>
            <span className={`w-7 shrink-0 text-center text-[11px] font-black ${rank <= 10 ? 'text-amber-300' : 'text-purple-300/60'}`}>{rank}º</span>
            <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full border border-amber-900/60 text-[10px]">
              <Face row={r} cls="h-full w-full" />
            </div>
            {medal?.(r) && (
              <img src={medal(r)} alt="" title="medalha do clã" data-testid={`gb-rank-medal-${idOf(r)}`}
                className="h-8 w-8 shrink-0 object-contain drop-shadow-[0_0_6px_rgba(255,190,60,.55)]" />
            )}
            <p className="min-w-0 flex-1 truncate text-[11px] font-black text-amber-100">{label(r)}</p>
            {rank <= 10 && <span className="shrink-0 text-[10px]">🏆</span>}
            <span className="shrink-0 text-[10px] font-black text-amber-300">{value(r)}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const Jackpot = ({ valor, partidas, shares }) => (
  <div className="relative mx-auto mb-2 flex w-fit max-w-full flex-col items-center rounded-2xl border border-amber-500/70 bg-black/70 px-5 pb-2 pt-1.5 backdrop-blur"
    style={{ animation: 'gb-jackpot-neon 2.2s ease-in-out infinite' }} data-testid="gb-pvp-jackpot">
    <img src="/gb/title_jackpot.webp" alt="JACKPOT" className="w-[180px] max-w-full drop-shadow-xl" />
    <p className="flex items-center gap-1 text-base font-black text-amber-200" data-testid="gb-pvp-jackpot-value">
      <GbCoin /> {gbFmt(valor)}
    </p>
    <p className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-500">acumulado do pvp</p>
    <p className="mt-1 text-center text-[10px] text-purple-200/70">
      +100 por duelo · {gbFmt(partidas)} duelos · top 3 leva {(shares || []).map((s) => `${s.pct}%`).join(' / ')}
    </p>
    <style>{`@keyframes gb-jackpot-neon {
      0%, 100% { box-shadow: 0 0 5px rgba(255,190,60,.35), inset 0 0 8px rgba(255,170,0,.08); }
      50% { box-shadow: 0 0 16px rgba(255,205,90,.85), 0 0 30px rgba(255,150,0,.45), inset 0 0 12px rgba(255,180,0,.18); }
    }`}</style>
  </div>
);

const RulesBtn = ({ onClick }) => (
  <button onClick={onClick} data-testid="gb-rank-rules-btn" title="Regras dos rankings"
    className="flex h-14 w-14 flex-none items-center justify-center transition-transform active:scale-90"
    style={{ animation: 'gb-rules-neon 2s ease-in-out infinite' }}>
    <img src="/gb/ic_regras.webp" alt="Regras do ranking" className="h-full w-full object-contain" />
    <style>{`@keyframes gb-rules-neon {
      0%, 100% { filter: drop-shadow(0 0 3px rgba(255,190,60,.5)); }
      50% { filter: drop-shadow(0 0 9px rgba(255,215,110,.95)) drop-shadow(0 0 18px rgba(255,150,0,.6)); }
    }`}</style>
  </button>
);

const Contrib = ({ clanId, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    gbApi.get(`/clans/${clanId}/contrib`).then(({ data }) => setD(data)).catch(() => setD({ members: [] }));
  }, [clanId]);

  return (
    <div className="pwa-safe-top fixed inset-0 z-[60] flex items-end justify-center bg-black/80 p-3 sm:items-center"
      data-testid="gb-clan-contrib">
      <div className="w-full max-w-md rounded-3xl border border-purple-700/60 bg-[#120a1d] p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-purple-400/70">contribuição do clã</p>
            <h3 className="flex items-center gap-2 truncate text-base font-black text-amber-200">
              {d?.clan?.medal_img && (
                <img src={d.clan.medal_img} alt="" className="h-8 w-8 object-contain" data-testid="gb-contrib-medal" />
              )}
              {d?.clan ? d.clan.name : 'carregando…'}
            </h3>
            {d?.clan && (
              <p className="text-[11px] text-purple-300/60">
                {gbFmt(d.clan.points)} pontos · {d.clan.members_count} membros
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="gb-clan-contrib-close"
            className="rounded-full border border-purple-700/60 p-2 text-purple-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-2 rounded-2xl border border-amber-800/40 bg-amber-950/20 px-3 py-2 text-[11px] text-amber-200/80">
          Se o clã terminar no top 3, o prêmio é dividido entre os <b>{d?.top_members || 5} que mais pontuaram</b>
          {' '}({(d?.shares || []).map((s) => `${s.rank}º ${s.pct}%`).join(' · ')}). Quem sai do clã perde os pontos.
        </p>

        {!d ? (
          <p className="flex items-center gap-2 py-6 text-xs text-purple-300/60"><Loader2 className="h-3.5 w-3.5 animate-spin" />carregando…</p>
        ) : (
          <div className="mt-3 max-h-[46vh] space-y-1 overflow-y-auto" data-testid="gb-clan-contrib-list">
            {(d.members || []).length === 0 && (
              <p className="py-4 text-center text-xs text-purple-300/60">Nenhum membro pontuou ainda.</p>
            )}
            {(d.members || []).map((m) => (
              <div key={m.kako_id} data-testid={`gb-contrib-row-${m.kako_id}`}
                className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 ${m.rank <= (d.top_members || 5)
                  ? 'border-amber-700/60 bg-gradient-to-r from-amber-950/50 to-black' : 'border-purple-900/70 bg-black/40'}`}>
                <span className={`w-6 text-center text-[11px] font-black ${m.rank <= 3 ? 'text-amber-300' : 'text-purple-300/60'}`}>{m.rank}º</span>
                <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-amber-900/60 text-[10px]">
                  <Face row={m} cls="h-full w-full" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-black text-amber-100">
                    {m.leader && <Crown className="mr-1 inline h-3 w-3 text-amber-400" />}{m.nickname}
                  </p>
                  <p className="text-[10px] text-purple-300/50">nível {m.level} · {gbFmt(m.pvp_wins)} vitórias PvP</p>
                </div>
                <span className="shrink-0 font-mono text-[11px] font-black text-amber-300">{gbFmt(m.points)} pts</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CAMPOS = {
  level: {
    idOf: (r) => r.kako_id,
    label: (r) => r.nickname,
    medal: (r) => r.clan?.medal_img,
    value: (r) => `nível ${r.level} · ${gbFmt(r.xp)} XP`,
    dica: 'XP é acumulativo e nunca cai. O PvP dá até 100 XP por partida; as fases dão pouco, só para treinar.',
    vazio: 'Ninguém pontuou ainda — seja o primeiro!',
  },
  pvp: {
    idOf: (r) => r.kako_id,
    label: (r) => r.nickname,
    medal: (r) => r.clan?.medal_img,
    value: (r) => `${gbFmt(r.pvp_wins)} vitórias`,
    dica: 'Vitórias em duelos ao vivo.',
    vazio: 'Nenhum duelo ganho ainda — entre na sala de combinação!',
  },
  clan: {
    idOf: (r) => r.clan_id,
    label: (r) => r.name,
    medal: (r) => r.medal_img,
    value: (r) => `${gbFmt(r.points)} pts`,
    dica: 'Pontos somados dos membros (toque no clã para ver a contribuição de cada um).',
    vazio: 'Nenhum clã fundado ainda.',
  },
};

export const GbRanking = ({ meuId }) => {
  const [sub, setSub] = useState('level');
  const [dados, setDados] = useState(null);
  const [load, setLoad] = useState(true);
  const [clan, setClan] = useState('');
  const [rules, setRules] = useState(false);

  const buscar = useCallback(async (kind, silencioso) => {
    if (!silencioso) setLoad(true);
    try {
      const { data } = await gbApi.get(`/ranking?kind=${kind}`);
      setDados(data);
    } catch (e) { setDados({ ranking: [] }); } finally { setLoad(false); }
  }, []);

  useEffect(() => { buscar(sub); }, [sub, buscar]);

  useEffect(() => {                     // jackpot do PvP ao vivo
    if (sub !== 'pvp') return;
    const t = setInterval(() => buscar('pvp', true), 20000);
    return () => clearInterval(t);
  }, [sub, buscar]);

  const c = CAMPOS[sub];
  const T = TITULOS[sub];
  const lista = (dados?.ranking || []).map((r) => (
    sub === 'clan' ? { ...r, avatar: r.leader_avatar } : r));

  // sua posição no ranking aberto (igual ao Oil): calculada da própria lista
  const minhaPos = sub !== 'clan' && meuId
    ? lista.findIndex((r) => String(r.kako_id) === String(meuId)) : -1;
  const eu = minhaPos >= 0 ? lista[minhaPos] : null;
  const acima = minhaPos > 0 ? lista[minhaPos - 1] : null;
  const falta = acima && eu
    ? (sub === 'pvp' ? Number(acima.pvp_wins || 0) - Number(eu.pvp_wins || 0)
      : Number(acima.xp || 0) - Number(eu.xp || 0)) : 0;

  return (
    <div className="pb-10" data-testid="gb-ranking">
      {/* ABAS: um ranking em cada aba, como no Oil Tycoon (espaço à direita para o X do painel) */}
      <div className="flex gap-1.5 pr-10 sm:pr-12" data-testid="gb-rank-subtabs">
        {SUBS.map(({ id, label, legenda, Icon }) => {
          const on = sub === id;
          return (
            <button key={id} onClick={() => setSub(id)} data-testid={`gb-rank-${id}`}
              className={`relative min-w-0 flex-1 overflow-hidden rounded-2xl border px-2 pb-2 pt-2.5 text-center transition-all duration-200 active:scale-[.97] ${on
                ? 'border-amber-300/80 bg-gradient-to-b from-orange-500 via-orange-600 to-amber-500 shadow-[0_8px_22px_rgba(255,120,20,.45)]'
                : 'border-purple-800/70 bg-gradient-to-b from-[#1b0f2c]/90 to-black/80 hover:border-purple-600/80'}`}>
              <Icon className={`mx-auto h-4 w-4 ${on ? 'text-[#2a1200]' : 'text-purple-300/70'}`} />
              <span className={`mt-1 block truncate text-[11px] font-black tracking-[0.12em] ${on ? 'text-[#2a1200]' : 'text-purple-100/85'}`}>
                {label}
              </span>
              <span className={`block truncate text-[8px] font-black uppercase tracking-[0.2em] ${on ? 'text-[#6b3200]' : 'text-purple-400/50'}`}>
                {legenda}
              </span>
              {on && <span className="absolute inset-x-0 bottom-0 h-[3px] bg-amber-200/90" />}
            </button>
          );
        })}
      </div>

      {/* título do ranking aberto */}
      <div className="mt-3 min-w-0">
        <h2 className="flex items-center gap-2 truncate text-lg font-black tracking-wide md:text-xl"
          data-testid="gb-rank-title">
          <T.Icon className="h-4 w-4 shrink-0 text-orange-400" />
          <span className="bg-gradient-to-r from-amber-200 via-orange-300 to-amber-200 bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(255,120,20,.35)]">
            {T.nome}
          </span>
        </h2>
        <p className="truncate text-[10px] font-black uppercase tracking-[0.2em] text-purple-300/55">{T.sub}</p>
      </div>
      <div className="mt-1.5 h-[2px] w-full rounded-full bg-gradient-to-r from-orange-500/80 via-purple-700/50 to-transparent" />

      {/* livro de regras do ranking aberto: abaixo da linha do título, à direita */}
      <div className="-mb-2 mt-1 flex justify-end pr-1">
        <RulesBtn onClick={() => setRules(true)} />
      </div>

      {rules && <GbRankRules kind={sub} dados={dados} onClose={() => setRules(false)} />}

      {sub === 'pvp' && (
        <Jackpot valor={dados?.jackpot || 0} partidas={dados?.matches || 0} shares={dados?.shares} />
      )}
      {load && (
        <p className="flex items-center gap-2 py-6 text-xs text-purple-300/60">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />carregando o ranking…
        </p>
      )}
      {!load && lista.length === 0 && (
        <p className="rounded-2xl border border-purple-900/70 bg-black/50 px-4 py-6 text-center text-xs font-bold text-purple-200/70"
          data-testid="gb-rank-empty">{c.vazio}</p>
      )}

      {!load && lista.length > 0 && (
        <>
          <Podium rows={lista.slice(0, 3)} label={c.label} value={c.value} medal={c.medal}
            onPick={sub === 'clan' ? (r) => setClan(r.clan_id) : undefined} />
          <RankList rows={lista.slice(3)} startAt={4} label={c.label} value={c.value} medal={c.medal}
            meuId={meuId} idOf={c.idOf}
            onPick={sub === 'clan' ? (r) => setClan(r.clan_id) : undefined} />
        </>
      )}

      {/* SUA POSIÇÃO — só nos rankings de jogador */}
      {!load && sub !== 'clan' && meuId && (
        <div className="mx-auto mt-3 max-w-[430px] rounded-2xl border-2 border-orange-600/70 bg-gradient-to-r from-[#24110a] via-black to-[#1a0a24] px-3 py-2.5"
          data-testid="gb-rank-me">
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-orange-400/90">sua posição</p>
          <div className="mt-1 flex items-center gap-2.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-orange-600/60 bg-orange-500/15 text-sm font-black text-amber-300"
              data-testid="gb-rank-me-pos">
              {eu ? `${minhaPos + 1}º` : '—'}
            </span>
            <div className="min-w-0 flex-1">
              {eu ? (
                <>
                  <p className="truncate text-xs font-black text-amber-100" data-testid="gb-rank-me-value">
                    {c.value(eu)}
                  </p>
                  {minhaPos === 0
                    ? <p className="text-[10px] font-bold text-amber-400">Você está no topo da arena! 🏆</p>
                    : <p className="text-[10px] text-purple-300/70">
                      Faltam <b className="text-amber-300">{gbFmt(Math.max(1, falta + 1))}</b>
                      {sub === 'pvp' ? ' vitória(s)' : ' XP'} para passar o {minhaPos}º
                    </p>}
                </>
              ) : (
                <p className="text-[11px] text-purple-200/75">
                  Você ainda não aparece no top 50. {sub === 'pvp'
                    ? 'Ganhe um duelo para entrar na lista.'
                    : 'Jogue uma fase ou um duelo para somar XP.'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="mx-auto mt-3 max-w-[430px] text-[11px] leading-snug text-purple-300/55"
        data-testid="gb-rank-dica">{c.dica}</p>

      {clan && <Contrib clanId={clan} onClose={() => setClan('')} />}
    </div>
  );
};
